<template>
  <div class="relative" ref="root">
    <button
      type="button"
      class="border rounded-lg shadow-sm px-3 py-1 flex items-center w-full sm:w-auto whitespace-nowrap"
      :class="value
        ? 'bg-white border-neutral-400 text-neutral-800'
        : 'bg-white border-neutral-300 bg-gradient-to-b from-white to-neutral-100 focus:to-white focus:border-neutral-400 text-dblue-500'"
      id="submitter-filter-button"
      @click="toggle"
    >
      <svg
        class="w-4 h-4 mr-1 text-dblue-400 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        ></path>
      </svg>
      <span v-if="!value" class="text-neutral-800">{{ $t("Submitter") }}</span>
      <span v-else class="text-neutral-800 font-medium max-w-[180px] truncate">{{ value.label }}</span>
      <span
        v-if="value"
        class="ml-1.5 -mr-1 p-0.5 rounded-full text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100"
        role="button"
        :aria-label="$t('Clear')"
        id="submitter-filter-clear"
        @click.stop="clear"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </span>
      <svg
        v-else
        class="w-4 h-4 ml-1 -mr-1"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </button>

    <transition name="popdown">
      <div
        v-if="isOpen"
        class="absolute left-0 mt-2 w-full sm:w-72 bg-white border border-dblue-100 rounded-lg shadow-lg z-[9999]"
        id="submitter-filter-panel"
      >
        <div class="p-2 border-b border-neutral-100">
          <input
            ref="search"
            v-model="query"
            type="text"
            class="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-neutral-400 placeholder-gray-400"
            :placeholder="$t('Search name, email or domain')"
            id="submitter-filter-search"
            @keydown.down.prevent="moveActive(1)"
            @keydown.up.prevent="moveActive(-1)"
            @keydown.enter.prevent="pickActive"
            @keydown.esc.prevent="close"
          />
        </div>

        <div class="max-h-72 overflow-y-auto py-1" id="submitter-filter-results">
          <div v-if="loading && !options.length" class="px-3 py-4 text-sm text-neutral-600 text-center">
            {{ $t("Searching") }}…
          </div>
          <div v-else-if="!options.length" class="px-3 py-4 text-sm text-neutral-600 text-center">
            {{ $t("No submitters found") }}
          </div>

          <template v-else>
            <template v-if="peopleOptions.length">
              <div class="px-3 pt-1.5 pb-0.5 text-xs font-semibold uppercase tracking-wide text-neutral-700">
                {{ $t("People") }}
              </div>
              <a
                href="#"
                v-for="option in peopleOptions"
                :key="'p-' + option.value"
                class="px-3 py-1.5 flex items-center gap-2.5 text-neutral-800 hover:no-underline hover:text-neutral-900"
                :class="{ 'bg-neutral-100': option.index === activeIndex }"
                @mouseenter="activeIndex = option.index"
                @click.prevent="pick(option)"
              >
                <avatar
                  class="avatar flex-shrink-0"
                  :size="24"
                  :src="option.avatarUrl"
                  :username="option.name || option.value"
                ></avatar>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm truncate">{{ option.name || $t("Anonymous") }}</span>
                  <span class="block text-xs text-neutral-600 truncate">{{ option.value }}</span>
                </span>
                <span class="text-xs text-neutral-600 flex-shrink-0">{{ option.count }}</span>
              </a>
            </template>

            <template v-if="domainOptions.length">
              <div class="px-3 pt-1.5 pb-0.5 text-xs font-semibold uppercase tracking-wide text-neutral-700">
                {{ $t("Companies") }}
              </div>
              <a
                href="#"
                v-for="option in domainOptions"
                :key="'d-' + option.value"
                class="px-3 py-1.5 flex items-center gap-2.5 text-neutral-800 hover:no-underline hover:text-neutral-900"
                :class="{ 'bg-neutral-100': option.index === activeIndex }"
                @mouseenter="activeIndex = option.index"
                @click.prevent="pick(option)"
              >
                <span class="w-6 h-6 rounded-full bg-neutral-100 text-neutral-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img
                    v-show="faviconState[option.value] === 'ok'"
                    :src="faviconUrl(option.value)"
                    class="w-4 h-4"
                    alt=""
                    @load="onFaviconLoad(option.value, $event)"
                    @error="onFaviconError(option.value)"
                  />
                  <svg v-if="faviconState[option.value] !== 'ok'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm truncate">{{ $t("Everyone at") }} {{ option.value }}</span>
                </span>
                <span class="text-xs text-neutral-600 flex-shrink-0">{{ option.count }}</span>
              </a>
            </template>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  props: ["board-id", "value"],
  data() {
    return {
      isOpen: false,
      query: "",
      loading: false,
      people: [],
      domains: [],
      faviconState: {},
      activeIndex: 0,
      fetchController: null,
      debounceTimer: null,
    };
  },
  computed: {
    peopleOptions() {
      return this.people.map((person, i) => ({
        type: "email",
        value: person.email,
        name: person.name,
        avatarUrl: person.avatar_url,
        count: person.count,
        label: person.name || person.email,
        index: i,
      }));
    },
    domainOptions() {
      const offset = this.peopleOptions.length;
      return this.domains.map((domain, i) => ({
        type: "domain",
        value: "@" + domain.domain,
        count: domain.count,
        label: "@" + domain.domain,
        index: offset + i,
      }));
    },
    options() {
      return [...this.peopleOptions, ...this.domainOptions];
    },
  },
  watch: {
    query() {
      this.activeIndex = 0;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchSubmitters(), 250);
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
    if (this.fetchController) this.fetchController.abort();
    window.removeEventListener("click", this.onOutsideClick, true);
  },
  methods: {
    toggle() {
      this.isOpen ? this.close() : this.open();
    },
    open() {
      this.isOpen = true;
      this.query = "";
      this.activeIndex = 0;
      window.addEventListener("click", this.onOutsideClick, true);
      this.fetchSubmitters();
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus();
      });
    },
    close() {
      this.isOpen = false;
      window.removeEventListener("click", this.onOutsideClick, true);
    },
    onOutsideClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.close();
      }
    },
    clear() {
      this.close();
      this.$emit("change", null);
    },
    pick(option) {
      this.close();
      this.$emit("change", {
        type: option.type,
        value: option.value.replace(/^@/, ""),
        label: option.label,
      });
    },
    pickActive() {
      const option = this.options[this.activeIndex];
      if (option) this.pick(option);
    },
    moveActive(step) {
      if (!this.options.length) return;
      const count = this.options.length;
      this.activeIndex = (this.activeIndex + step + count) % count;
    },
    faviconUrl(domainValue) {
      const domain = domainValue.replace(/^@/, "");
      return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
    },
    onFaviconLoad(domainValue, event) {
      // Google's favicon service returns its generic 16px globe when the
      // domain has no real favicon; we asked for 64px, so a small image
      // means "none" — keep our own globe placeholder in that case.
      const isReal = event.target.naturalWidth >= 32;
      this.$set(this.faviconState, domainValue, isReal ? "ok" : "none");
    },
    onFaviconError(domainValue) {
      this.$set(this.faviconState, domainValue, "none");
    },
    async fetchSubmitters() {
      if (this.fetchController) this.fetchController.abort();
      this.fetchController = new AbortController();
      this.loading = true;

      const params = new URLSearchParams();
      if (this.query.trim()) params.set("q", this.query.trim());
      if (this.boardId) params.set("board_id", this.boardId);

      try {
        const response = await fetch(
          `/submitters?${params}`,
          {
            headers: { Accept: "application/json" },
            signal: this.fetchController.signal,
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        this.people = data.submitters || [];
        this.domains = data.domains || [];
        this.activeIndex = 0;
        this.loading = false;
      } catch (error) {
        if (error.name === "AbortError") return;
        this.people = [];
        this.domains = [];
        this.loading = false;
      }
    },
  },
};
</script>
